import { inject, Injectable, Signal, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, filter, firstValueFrom, map, take } from 'rxjs';
import { ProtocolCardActionTaken } from 'src/app/shared/card-regenerative-protocol/card-regenerative-protocol.component';
import { environment } from '../../../environments/environment';
import {
  RegenerativeProtocol,
  RegenerativeProtocolCreate,
  RegenerativeProtocolUpdate,
} from '../_models/regenerative/regenerativeProtocol';

export type ProtocolsState =
  | { status: 'idle' } // Not loaded yet
  | { status: 'loading' } // Currently fetching
  | { status: 'loaded'; data: RegenerativeProtocol[] } // Loaded successfully
  | { status: 'error'; error: unknown }; // Error during fetch

@Injectable({
  providedIn: 'root',
})
export class ProtocolsRegenerativeService {
  private regenerativeService = environment.regenerativeServiceUrl;
  public currentEditProtocol: RegenerativeProtocol = null;
  // Complete a Draft, Edit a Published or Watch Details --> used for managed the stepper for the different use cases
  public actionOnCurrentProtocol: ProtocolCardActionTaken = null;

  public $protocolsState = new BehaviorSubject<ProtocolsState>({ status: 'idle' });
  public $protocolsList: BehaviorSubject<RegenerativeProtocol[]> = new BehaviorSubject<
    RegenerativeProtocol[]
  >(null);

  // Signal to control the disabled state of the CONTINUE button in Protocol Creation
  private _isContinueButtonDisabled = signal<boolean>(true);

  // Read-only Signal to expose the state
  readonly isContinueButtonDisabled: Signal<boolean> = this._isContinueButtonDisabled.asReadonly();

  http = inject(HttpClient);
  constructor() {}

  /**
   * @description Get all protocols directly from the API and update state.
   * @returns {Promise<{ data: RegenerativeProtocol[], totalCount: number }>}
   */
  async getProtocols(): Promise<{ data: RegenerativeProtocol[]; totalCount: number }> {
    const response: { data: RegenerativeProtocol[]; totalCount: number } = await firstValueFrom(
      this.http.get<{ data: RegenerativeProtocol[]; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/protocols`
      )
    );
    this.$protocolsState.next({ status: 'loaded', data: response.data });
    return response;
  }

  /**
   * @description Ensures protocols are loaded. Returns cached protocols if available, otherwise fetches from API.
   * @returns {Promise<RegenerativeProtocol[]>} The list of protocols
   */
  async ensureProtocolsLoaded(): Promise<RegenerativeProtocol[]> {
    const state = this.$protocolsState.getValue();

    if (state.status === 'loaded') {
      return state.data;
    }

    if (state.status === 'loading') {
      // Wait until loading finishes
      return firstValueFrom(
        this.$protocolsState.pipe(
          filter(
            (s): s is { status: 'loaded'; data: RegenerativeProtocol[] } => s.status === 'loaded'
          ),
          take(1),
          map((s) => s.data)
        )
      );
    }

    // idle or error → fetch
    this.$protocolsState.next({ status: 'loading' });
    try {
      const response = await this.getProtocols();
      return response.data;
    } catch (error) {
      this.$protocolsState.next({ status: 'error', error });
      throw error;
    }
  }

  /**
   * @description Returns a specific protocol by ID safely. Ensures protocols are loaded first.
   * @param {string} protocolId - The ID of the protocol
   * @returns {Promise<RegenerativeProtocol | null>} The protocol or null if not found
   */
  async getProtocolById(protocolId: string): Promise<RegenerativeProtocol | null> {
    const protocols = await this.ensureProtocolsLoaded();
    return protocols.find((p) => p.id === protocolId) ?? null;
  }

  /**
   * @description Save a new protocol.
   * @param {RegenerativeProtocolCreate} protocol - The protocol data to create.
   * @returns {Promise<{ data: RegenerativeProtocol, totalCount: number }>} A promise containing the created protocol and the total count.
   */
  async saveProtocol(
    protocol: RegenerativeProtocolCreate
  ): Promise<{ data: RegenerativeProtocol; totalCount: number }> {
    return firstValueFrom(
      this.http.post<{ data: RegenerativeProtocol; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/protocols`,
        protocol
      )
    );
  }

  /**
   * @description Update an existing protocol.
   * @param {string} protocolId - The ID of the protocol to update.
   * @param {RegenerativeProtocolUpdate} protocol - The updated protocol data.
   * @returns {Promise<{ data: RegenerativeProtocol, totalCount: number }>} A promise containing the updated protocol and the total count.
   */
  async updateProtocol(
    protocolId: string,
    protocol: RegenerativeProtocolUpdate
  ): Promise<{ data: RegenerativeProtocol; totalCount: number }> {
    return firstValueFrom(
      this.http.put<{ data: RegenerativeProtocol; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/protocols/${protocolId}`,
        protocol
      )
    );
  }

  /**
   * @description Save multiple protocols in bulk.
   * @param {RegenerativeProtocolCreate[]} protocol - The array of protocols to create.
   * @returns {Promise<RegenerativeProtocol[]>} A promise containing the list of created protocols.
   */
  async saveProtocolBulk(protocol: RegenerativeProtocolCreate[]): Promise<RegenerativeProtocol[]> {
    return firstValueFrom(
      this.http.post<RegenerativeProtocol[]>(
        `${this.regenerativeService}api/private/v1/protocols`,
        protocol
      )
    );
  }

  /**
   * @description Update multiple existing protocols in bulk.
   * @param {RegenerativeProtocolUpdate[]} protocol - The array of protocols to update.
   * @returns {Promise<RegenerativeProtocol[]>} A promise containing the list of updated protocols.
   */
  async updateProtocolBulk(
    protocol: RegenerativeProtocolUpdate[]
  ): Promise<RegenerativeProtocol[]> {
    return firstValueFrom(
      this.http.put<RegenerativeProtocol[]>(
        `${this.regenerativeService}api/private/v1/protocols`,
        protocol
      )
    );
  }

  /**
   * @description Delete a specific protocol by ID.
   * @param {string} protocolId - The ID of the protocol to delete.
   * @returns {Promise<{}>} A promise containing an empty object upon successful deletion.
   */
  async deleteProtocol(protocolId: string): Promise<{}> {
    return firstValueFrom(
      this.http.delete<{}>(`${this.regenerativeService}api/private/v1/protocols/${protocolId}`)
    );
  }

  /**
   * @description Upload the cover image of a protocol into a public bucket.
   * @param {string} protocolId - The ID of the protocol.
   * @param {any} imageDataObj - An object containing the image in bytes and its name.
   * @returns {Promise<{ data: { imgUrl: string }[]; totalCount: number }>} A promise containing the image URLs and total count.
   */
  async uploadProtocolCoverImage(
    protocolId: string,
    imageDataObj: any
  ): Promise<{ data: { imgUrl: string }[]; totalCount: number }> {
    const formData: FormData = new FormData();
    formData.append('file', imageDataObj);
    return firstValueFrom(
      this.http.post<{ data: { imgUrl: string }[]; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/protocols/uploadImage/${protocolId}`,
        formData
      )
    );
  }

  // Method to update the state from any stepper component
  /**
   * @description Method to update the state for CONTINUE btn disability, from any of the stepper component
   * @param {boolean} state - The state to set for the CONTINUE button.
   * @returns {void} It updates the state for the CONTINUE button disability.
   */
  setContinueButtonDisabled(state: boolean): void {
    this._isContinueButtonDisabled.set(state);
  }
}
